import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaArrowRight, FaShieldAlt, FaFlask, FaUsers, FaMicroscope, FaRegLightbulb, 
  FaAward, FaClinicMedical, FaStar, FaHeartbeat, FaBaby, FaFemale, FaBone,
  FaTablets, FaCapsules, FaUserMd
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import Hero from '../components/sections/Hero';

const Home = () => {
  // State to track image loading errors
  const [imageErrors, setImageErrors] = useState({
    facility: false,
    productImages: {}
  });

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };


  const specialties = [
    {
      id: 'pediatrics',
      name: 'Pediatrics',
      icon: <FaBaby className="text-pediatric text-3xl" />,
      description: 'Specialized formulations designed for children to support their growth and well-being.',
      color: 'bg-pediatric',
      link: '/products/pediatrics'
    },
    {
      id: 'gynecology',
      name: 'Gynecology',
      icon: <FaFemale className="text-gynecology text-3xl" />,
      description: 'Advanced healthcare solutions for women at every stage of life.',
      color: 'bg-gynecology',
      link: '/products/gynecology'
    },
    {
      id: 'orthopedics',
      name: 'Orthopedics',
      icon: <FaBone className="text-orthopedic text-3xl" />,
      description: 'Innovative treatments for bone and joint health to improve mobility and quality of life.',
      color: 'bg-orthopedic',
      link: '/products/orthopedics'
    }
  ];

  const testimonials = [
    {
      id: 1,
      text: "Migliore Life Sciences products have become an essential part of my practice. Their commitment to quality and innovation is evident in every product.",
      author: "Dr. Priya Sharma",
      position: "Pediatrician",
      avatar: <FaUserMd className="text-primary text-3xl" />
    },
    {
      id: 2,
      text: "As a gynecologist, I've seen remarkable results with Migliore's products. They understand the unique needs of women's healthcare.",
      author: "Dr. Anand Mehta",
      position: "Gynecologist",
      avatar: <FaUserMd className="text-gynecology text-3xl" />
    },
    {
      id: 3,
      text: "The orthopedic range from Migliore has helped many of my patients regain mobility and comfort. Highly recommended for orthopedic specialists.",
      author: "Dr. Rajiv Kumar",
      position: "Orthopedic Surgeon",
      avatar: <FaUserMd className="text-orthopedic text-3xl" />
    }
  ];

  // Featured products with backup icons
  const featuredProducts = [
    {
      id: 'flora-123',
      name: 'FLORA-123',
      category: 'Pediatric',
      categoryColor: 'bg-pediatric/10 text-pediatric',
      type: 'Probiotic',
      description: 'Triple-strain probiotic formulation for optimal digestive health in children.',
      image: '/product_img/PEDIATRIC/FLORA-123.png',
      icon: <FaTablets className="text-pediatric text-4xl" />,
      link: '/products/pediatrics'
    },
    {
      id: 'calijoy-hd',
      name: 'CALIJOY-HD',
      category: 'Gynecology',
      categoryColor: 'bg-gynecology/10 text-gynecology',
      type: 'Nutritional',
      description: 'Advanced calcium supplement with Vitamin D3 for women, supporting bone health.',
      image: '/product_img/GYNAECOLOGY/CALIJOY-HD.png',
      icon: <FaCapsules className="text-gynecology text-4xl" />,
      link: '/products/gynecology'
    },
    {
      id: 'ortho',
      name: 'ORTHO',
      category: 'Orthopedic',
      categoryColor: 'bg-orthopedic/10 text-orthopedic',
      type: 'Orthopedic',
      description: 'Specialized orthopedic supplement for bone and joint health concerns.',
      image: '/product_img/ORTHOPEDIC/ORTHO.png',
      icon: <FaBone className="text-orthopedic text-4xl" />,
      link: '/products/orthopedics'
    }
  ];

  // Handler for image loading errors
  const handleImageError = (id) => {
    if (id === 'facility') {
      setImageErrors(prev => ({ ...prev, facility: true }));
    } else {
      setImageErrors(prev => ({
        ...prev,
        productImages: { ...prev.productImages, [id]: true }
      }));
    }
  };

  return (
    <div>
      <Hero 
        title="Advancing Healthcare Through Innovation"
        subtitle="Migliore Life Sciences is dedicated to providing quality pharmaceutical products across Pediatrics, Gynecology, and Orthopedics."
        ctaText="Explore Our Products"
        ctaLink="/products"
        backgroundImage="https://placehold.co/1920x1080/0066CC/FFFFFF?text=Migliore+Life+Sciences"
      />
      
      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-r from-blue-50 to-blue-100">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-lg text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <div className="text-4xl font-bold text-primary mb-2">12+</div>
              <div className="text-gray-600">Years of Excellence</div>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-lg text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="text-4xl font-bold text-primary mb-2">28</div>
              <div className="text-gray-600">Trusted Brands</div>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-lg text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="text-4xl font-bold text-primary mb-2">100+</div>
              <div className="text-gray-600">Healthcare Partners</div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* About Section */}
      <motion.section 
        className="py-20 relative overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInUp}
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/5 rounded-full translate-y-1/3 -translate-x-1/3"></div>
        
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                <span className="text-primary">12 Years</span> of Excellence in Pharmaceutical Care
              </h2>
              <p className="text-gray-600 mb-6 text-lg">
                Established in 2012, Migliore Life Sciences has grown to become a trusted name in the pharmaceutical industry in India. Our commitment to quality and innovation has led us to develop 28 trusted brands across our core specializations.
              </p>
              <p className="text-gray-600 mb-8 text-lg">
                We are a marketing-driven pharmaceutical company focused on delivering specialized healthcare solutions that meet the specific needs of doctors and patients.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/about" className="btn-primary">
                  Learn More About Us
                </Link>
                <Link to="/products" className="btn-outline-primary">
                  View Our Products
                </Link>
              </div>
            </div>
            <motion.div 
              className="rounded-lg overflow-hidden shadow-xl relative z-10 p-8 flex flex-col items-center justify-center min-h-[300px] lg:min-h-full bg-gradient-to-br from-primary-light to-secondary-light text-white"
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {/* Background decorative circles */}
              <div className="absolute top-0 left-0 w-3/4 h-3/4 bg-white/10 rounded-full blur-2xl opacity-50 -translate-x-1/4 -translate-y-1/4"></div>
              <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-white/5 rounded-full blur-xl opacity-40 translate-x-1/4 translate-y-1/4"></div>

              {/* Foreground content */}
              <div className="relative z-10 flex flex-col items-center">
                <div className="p-6 bg-white/30 backdrop-blur-md rounded-full shadow-xl mb-6">
                  <FaRegLightbulb className="text-6xl text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-center">Pioneering Innovation</h3>
                <p className="text-center text-gray-100 max-w-xs">
                  Advancing healthcare through cutting-edge research and development.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
      
      {/* Our Core Values */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at Migliore Life Sciences
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6 mx-auto">
                <FaShieldAlt className="text-primary text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Quality-Driven</h3>
              <p className="text-gray-600 text-center">
                We are committed to maintaining the highest standards in all our products and services.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mb-6 mx-auto">
                <FaUsers className="text-secondary text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Doctor-Focused</h3>
              <p className="text-gray-600 text-center">
                We develop solutions that help healthcare professionals provide the best care for their patients.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6 mx-auto">
                <FaRegLightbulb className="text-primary text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Ethical & Sustainable</h3>
              <p className="text-gray-600 text-center">
                We believe in responsible business practices that benefit society and the environment.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Our Specialties */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Specialties</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our specialized pharmaceutical solutions in three key therapeutic areas
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {specialties.map((specialty, index) => (
              <motion.div
                key={specialty.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <div className={`h-3 ${specialty.color}`}></div>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mr-4">
                      {specialty.icon}
                    </div>
                    <h3 className="text-xl font-bold">{specialty.name}</h3>
                  </div>
                  <p className="text-gray-600 mb-6">{specialty.description}</p>
                  <Link to={specialty.link} className="text-primary font-medium flex items-center hover:underline">
                    View Products <FaArrowRight className="ml-2 text-sm" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Featured Products Slider */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Featured Products</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our most popular pharmaceutical solutions
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all"
              >
                <div className="h-56 bg-gray-50 flex items-center justify-center p-4 relative">
                  {!imageErrors.productImages[product.id] ? (
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="max-h-full max-w-full object-contain"
                      onError={() => handleImageError(product.id)}
                    />
                  ) : (
                    product.icon
                  )}
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className={`text-xl font-bold text-${product.category.toLowerCase()}`}>{product.name}</h3>
                    <span className={`${product.categoryColor} text-xs px-2 py-1 rounded-full`}>
                      {product.type}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">
                    {product.description}
                  </p>
                  <Link to={product.link} className={`text-${product.category.toLowerCase()} font-medium flex items-center hover:underline`}>
                    Learn More <FaArrowRight className="ml-2 text-sm" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonial Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Healthcare Professionals Say</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trusted by doctors across specialties
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all border border-gray-100"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 mr-1" />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-6">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mr-4">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="font-bold">{testimonial.author}</h4>
                    <p className="text-gray-500 text-sm">{testimonial.position}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Migliore Life Sciences</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence sets us apart in the pharmaceutical industry
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <FaMicroscope className="text-primary text-xl" />
              </div>
              <h3 className="text-lg font-bold mb-3">Research-Backed Products</h3>
              <p className="text-gray-600">Our formulations are developed based on extensive research and clinical validation.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <FaShieldAlt className="text-primary text-xl" />
              </div>
              <h3 className="text-lg font-bold mb-3">Quality Assurance</h3>
              <p className="text-gray-600">Rigorous quality control measures to ensure product safety and efficacy.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <FaAward className="text-primary text-xl" />
              </div>
              <h3 className="text-lg font-bold mb-3">Industry Recognition</h3>
              <p className="text-gray-600">Award-winning formulations recognized for their innovation and effectiveness.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <FaClinicMedical className="text-primary text-xl" />
              </div>
              <h3 className="text-lg font-bold mb-3">Doctor Preferred</h3>
              <p className="text-gray-600">Trusted by healthcare professionals for their practice and patient care.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <FaHeartbeat className="text-primary text-xl" />
              </div>
              <h3 className="text-lg font-bold mb-3">Patient-Centric</h3>
              <p className="text-gray-600">Developed with patient well-being and comfort as our highest priority.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <FaFlask className="text-primary text-xl" />
              </div>
              <h3 className="text-lg font-bold mb-3">Innovative Formulations</h3>
              <p className="text-gray-600">Continuously improving our products with the latest pharmaceutical advancements.</p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Contact CTA */}
      <motion.section 
        className="py-20 bg-gradient-to-b from-white to-gray-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInUp}
      >
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Learn More?</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
            Contact our team to discuss how Migliore Life Sciences can support your healthcare needs.
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/contact" className="btn-primary text-lg px-8 py-4">
              Contact Us Today
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Home;
